import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { hasRoleLevel, type MaintenanceCandidate } from '@course-authoring/shared-types';
import { AdminPage, AdminPageHeader, AdminStatusMessage, adminFocusable, adminTokens as a } from '../admin-ui';
import { useAuth } from '../auth';
import { fetchMaintenanceInventory } from '../api/maintenanceRepublishApi';
import { maintenanceItemKey, orderMaintenanceItems, runMaintenanceBatch, type MaintenanceOutcome } from '../api/maintenanceBatch';
import { QuestionnaireRepublishInventory } from './QuestionnaireRepublishInventory';
import { formatDateTime } from '../utils/formatDateTime';
import { republishStyles as styles } from './republishStyles';

// The tab pair is an existing either/or view switch, so it renders as a segmented control.
const tabStyles: Record<string, CSSProperties> = {
  list: { display: 'inline-flex', alignSelf: 'flex-start', gap: 4, padding: 4, borderRadius: a.radius.pill, backgroundColor: a.surface.subtle },
  tab: {
    ...adminFocusable,
    minHeight: '34px',
    padding: '0 16px',
    border: 'none',
    borderRadius: a.radius.pill,
    backgroundColor: 'transparent',
    color: a.text.secondary,
    fontFamily: 'inherit',
    fontSize: a.typography.body.size,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tabSelected: { backgroundColor: a.action.primary, color: a.text.onPrimary, fontWeight: 700 },
};
const date = (value: string | null | undefined) => value ? formatDateTime(value) : 'Unknown';
const deliveryPath = (item: MaintenanceCandidate) => `/${item.kind === 'lesson' ? 'lessons' : 'courses'}/${item.id}/delivery`;

export function AdminRepublishPage() {
  const { user } = useAuth();
  if (!user || !hasRoleLevel(user.role, 'super_admin')) {
    return (
      <AdminPage>
        <AdminStatusMessage tone="error" role="alert">This page requires super administrator access.</AdminStatusMessage>
      </AdminPage>
    );
  }
  return <RepublishTabs exportedBy={user.id} />;
}

function RepublishTabs({ exportedBy }: { exportedBy: string }) {
  const [tab, setTab] = useState<'content' | 'questionnaires'>('content');
  const [questionnairesVisited, setQuestionnairesVisited] = useState(false);
  const [running, setRunning] = useState(false);
  return <AdminPage maxWidth="1200px">
    <AdminPageHeader headingLevel="h1" title="Republish content" />
    <div role="tablist" aria-label="Content type" style={tabStyles.list}>
      <button id="content-tab" role="tab" aria-selected={tab === 'content'} aria-controls="content-panel" disabled={running} style={{ ...tabStyles.tab, ...(tab === 'content' ? tabStyles.tabSelected : {}) }} onClick={() => setTab('content')}>Lessons &amp; Courses</button>
      <button id="questionnaires-tab" role="tab" aria-selected={tab === 'questionnaires'} aria-controls="questionnaires-panel" disabled={running} style={{ ...tabStyles.tab, ...(tab === 'questionnaires' ? tabStyles.tabSelected : {}) }} onClick={() => { setQuestionnairesVisited(true); setTab('questionnaires'); }}>Questionnaires</button>
    </div>
    <div id="content-panel" role="tabpanel" aria-labelledby="content-tab" hidden={tab !== 'content'}>
      <RepublishInventory exportedBy={exportedBy} active={tab === 'content'} onRunningChange={setRunning} />
    </div>
    <div id="questionnaires-panel" role="tabpanel" aria-labelledby="questionnaires-tab" hidden={tab !== 'questionnaires'}>
      {questionnairesVisited && <QuestionnaireRepublishInventory active={tab === 'questionnaires'} onRunningChange={setRunning} />}
    </div>
  </AdminPage>;
}

function RepublishInventory({ exportedBy, active, onRunningChange }: { exportedBy: string; active: boolean; onRunningChange: (running: boolean) => void }) {
  const [filterByAge, setFilterByAge] = useState(true);
  const [page, setPage] = useState(1);
  const [generation, setGeneration] = useState(0);
  const cutoff = useRef<string | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => { if (!active) setSelected(new Set()); }, [active]);
  const [outcomes, setOutcomes] = useState<MaintenanceOutcome[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const busy = useRef(false);
  const activeBatch = useRef<AbortController | null>(null);
  useEffect(() => () => { activeBatch.current?.abort(); }, []);
  const inventory = useQuery({
    queryKey: ['maintenanceRepublish', page, generation, filterByAge],
    queryFn: async () => {
      const data = await fetchMaintenanceInventory(page, cutoff.current, filterByAge);
      cutoff.current = data.cutoff;
      return data;
    },
    retry: false, refetchOnWindowFocus: false, enabled: active,
  });
  const items = inventory.data?.items ?? [];
  const selectable = items.filter(item => item.run?.state !== 'running');
  const selectedItems = selectable.filter(item => selected.has(maintenanceItemKey(item)));

  const start = async (batch: MaintenanceCandidate[], retry = false) => {
    if (busy.current || batch.length === 0) return;
    busy.current = true; setRunning(true); onRunningChange(true); setSelected(new Set());
    const controller = new AbortController(); activeBatch.current = controller;
    setOutcomes(orderMaintenanceItems(batch).map(item => ({ item, status: 'pending' })));
    try {
      await runMaintenanceBatch(batch, exportedBy, {
        signal: controller.signal, retry,
        onProgress: setProgress,
        onOutcome: outcome => {
          if (!controller.signal.aborted) setOutcomes(current => current.map(row => maintenanceItemKey(row.item) === maintenanceItemKey(outcome.item) ? outcome : row));
        },
        onItemSettled: () => inventory.refetch(),
      });
    } finally {
      busy.current = false;
      if (!controller.signal.aborted) { setRunning(false); onRunningChange(false); setProgress(''); }
    }
  };
  const changePage = (next: number) => { setSelected(new Set()); setPage(next); };

  return <div>
    <p style={styles.note}>Published, locked lessons and courses. When the age filter is checked, only items older than one hour or with recorded failures are included. Oldest publication first.</p>
    <p style={styles.note}>Keep this tab open. Selected items run one at a time, lessons before courses, for Standard and all assigned accounts. Reloading does not resume the batch.</p>
    <p style={styles.note}>An incomplete recorded delivery stays in progress until more than ten minutes pass without recorded activity. Refresh to check again. With the age filter checked, recent successful republishes stay out of the list for one hour; failure history is limited to retained records.</p>
    <div style={styles.controls}>
      <label><input type="checkbox" checked={filterByAge} disabled={running || inventory.isFetching} onChange={event => { setFilterByAge(event.target.checked); cutoff.current = undefined; setSelected(new Set()); setPage(1); setGeneration(value => value + 1); }} /> Exclude content published less than 1 hour ago</label>
      <button style={styles.button} disabled={running || inventory.isFetching} onClick={() => { cutoff.current = undefined; setSelected(new Set()); setPage(1); setGeneration(value => value + 1); }}>Refresh inventory</button>
      <button style={styles.button} disabled={running || inventory.isFetching || selectedItems.length === 0} onClick={() => void start(selectedItems)}>Republish selected ({selectedItems.length})</button>
      {inventory.data && <span>{inventory.data.totalCount} items · {inventory.data.lessonCount} lessons · {inventory.data.courseCount} courses</span>}
    </div>
    {inventory.data && <p style={styles.note}>{filterByAge && <>Publication cutoff: {date(inventory.data.cutoff)} · </>}Last checked: {date(inventory.data.evaluatedAt)}</p>}
    {inventory.isFetching && <p role="status">Loading inventory…</p>}
    {inventory.isError && <p role="alert">{inventory.error instanceof Error ? inventory.error.message : 'Inventory unavailable.'}</p>}
    <div style={styles.tableFrame}>
      <table style={styles.table}>
        <thead><tr>
          <th style={styles.cell}><input type="checkbox" aria-label="Select visible items" disabled={running || selectable.length === 0}
            checked={selectable.length > 0 && selectable.every(item => selected.has(maintenanceItemKey(item)))}
            onChange={event => setSelected(event.target.checked ? new Set(selectable.map(maintenanceItemKey)) : new Set())} /></th>
          <th style={styles.cell}>Content</th><th style={styles.cell}>Last published</th><th style={styles.cell}>Assigned accounts</th><th style={styles.cell}>Recorded delivery</th>
        </tr></thead>
        <tbody>{items.map(item => <tr key={maintenanceItemKey(item)}>
          <td style={styles.cell}><input type="checkbox" aria-label={`Select ${item.kind}: ${item.title}`} disabled={running || item.run?.state === 'running'}
            checked={selected.has(maintenanceItemKey(item))} onChange={event => setSelected(current => { const next = new Set(current); if (event.target.checked) next.add(maintenanceItemKey(item)); else next.delete(maintenanceItemKey(item)); return next; })} /></td>
          <td style={styles.cell}><strong>{item.title}</strong><div style={styles.note}>{item.kind} · {item.id}</div><Link to={deliveryPath(item)}>View delivery status</Link></td>
          <td style={styles.cell}>{date(item.lastPublishedAt)}</td><td style={styles.cell}>{item.assignedAccountCount} + Standard</td>
          <td style={styles.cell}>
            {item.run?.state === 'running' && <strong>In progress</strong>}
            {item.run?.state === 'stalled' && <strong>Interrupted delivery</strong>}
            {item.run && <div style={styles.note}>{item.run.recordedScopeCount}/{item.run.intendedScopeCount} targets recorded · Last activity {date(item.run.lastActivityAt)}</div>}
            {item.runError && <p>Run error: {item.runError}</p>}
            {item.failures.length > 0 && <details><summary>Recorded failures ({item.failures.length})</summary>{item.failures.map(failure => <p key={failure.externalAccountId ?? 'standard'}>
              {failure.externalAccountId === null ? 'Standard' : `${failure.accountSlug} (${failure.externalAccountId})`}: {failure.error ?? 'Delivery failed'} · {date(failure.recordedAt)}
            </p>)}</details>}
            {!item.run && !item.failures.length && <span style={styles.note}>No retained delivery evidence</span>}
          </td>
        </tr>)}</tbody>
      </table>
    </div>
    {inventory.data?.totalCount === 0 && <p>No eligible content needs republishing.</p>}
    <div style={styles.controls}>
      <button style={styles.button} disabled={running || inventory.isFetching || page <= 1} onClick={() => changePage(page - 1)}>Previous</button>
      <span>Page {page} of {Math.max(1, Math.ceil((inventory.data?.totalCount ?? 0) / (inventory.data?.pageSize ?? 25)))}</span>
      <button style={styles.button} disabled={running || inventory.isFetching || !inventory.data || page * inventory.data.pageSize >= inventory.data.totalCount} onClick={() => changePage(page + 1)}>Next</button>
    </div>
    {outcomes.length > 0 && <section aria-label="Batch results"><h2 style={styles.sectionTitle}>Batch results</h2>
      <p role="status">{running ? progress : 'Batch finished. Review the results below.'}</p>
      {outcomes.map(outcome => <article style={styles.outcome} key={maintenanceItemKey(outcome.item)}>
        <strong>{outcome.item.title} · {outcome.status}</strong>
        {outcome.message && <p>{outcome.message}</p>}
        {outcome.totalMs !== undefined && <p style={styles.note}>{outcome.targetCount ?? 0} targets · Publication {((outcome.publicationMs ?? 0) / 1000).toFixed(1)}s · Delivery {((outcome.deliveryMs ?? 0) / 1000).toFixed(1)}s · Total {(outcome.totalMs / 1000).toFixed(1)}s</p>}
        {outcome.results?.map((result, index) => <p key={`${result.accountSlug}:${index}`}>{result.accountSlug}: {result.success ? 'Delivered' : result.error ?? 'Failed'}{result.warning ? ` · ${result.warning}` : ''}</p>)}
        <Link to={deliveryPath(outcome.item)}>View delivery status</Link>{' '}
        {outcome.status === 'failed' && <button style={styles.button} disabled={running || inventory.isFetching || items.find(item => maintenanceItemKey(item) === maintenanceItemKey(outcome.item))?.run?.state === 'running'} onClick={() => void start([outcome.item], true)}>Retry whole item</button>}
      </article>)}
    </section>}
  </div>;
}
